import { useParams } from 'react-router-dom';
import { useAddCaseFormContext } from '../contexts/AddCaseFormContext';
import useCaseFieldUpdate from './useCaseFieldUpdate';
import { MEDICAL_PROVIDER_INPUT_MODE } from '@demandproai/shared';
import usePlaintiffs from './usePlaintiffs';
import { useMemo } from 'react';

/**
 * Reads and toggles the medical providers input mode (AI or MANUAL) for the
 * active client. Flipping persists to the case and updates formik so both
 * timing sections re-render in the new mode.
 * @returns {{ mode: ('AI'|'MANUAL'), isAiMode: boolean, toggleMode: Function }}
 */
const useMedicalProvidersInputMode = (isPostAccident) => {
  const { formik, selectedTiffIndex } = useAddCaseFormContext();
  const { caseId } = useParams();
  const { updateCase } = useCaseFieldUpdate({ caseId });
  const { plaintiffs, hasAnyPlaintiffMedicalRecords } = usePlaintiffs();

  const mode = formik.values.clients?.[selectedTiffIndex]?.medicalProvidersInputMode || MEDICAL_PROVIDER_INPUT_MODE.AI;

  const toggleMode = () => {
    const nextMode = mode === MEDICAL_PROVIDER_INPUT_MODE.AI ? MEDICAL_PROVIDER_INPUT_MODE.MANUAL : MEDICAL_PROVIDER_INPUT_MODE.AI;
    // apply the mode change to every plaintiff so the toggle stays in sync across clients
    const updatePayload = plaintiffs.reduce((acc, _client, index) => {
      const fieldPath = `clients.${index}.medicalProvidersInputMode`;
      formik.setFieldValue(fieldPath, nextMode);
      acc[fieldPath] = nextMode;
      return acc;
    }, {});
    updateCase({ data: updatePayload });
  };

  const isToggleAvailiable = useMemo(()=>{
    return !hasAnyPlaintiffMedicalRecords && isPostAccident;
  }, [hasAnyPlaintiffMedicalRecords, isPostAccident]);

  return { 
    mode, 
    isAiMode: mode === MEDICAL_PROVIDER_INPUT_MODE.AI, 
    toggleMode,
    isToggleAvailiable
  };
};

export default useMedicalProvidersInputMode;
